import React, { useState, useRef } from 'react';
import { initializeApp } from 'firebase/app';
import {
  getFirestore,
  collection,
  addDoc,
  serverTimestamp,
} from 'firebase/firestore';
import assetData from './refinery-assets.json';

const firebaseConfig = {
  apiKey: "AIzaSyBALO-oNqvxmRhTSx9S54A5PIilTrxVOYM",
  authDomain: "refinery-desk-dashboard.firebaseapp.com",
  projectId: "refinery-desk-dashboard",
  storageBucket: "refinery-desk-dashboard.firebasestorage.app",
  messagingSenderId: "28035012838",
  appId: "1:28035012838:web:eb80f171a10371cdbec327"
};

const app = initializeApp(firebaseConfig);
const db = getFirestore(app);

export default function Calendar({ userId, selectedCustomer, onBack }) {
  const [columns, setColumns] = useState(4);
  const [successMessage, setSuccessMessage] = useState('');

  const scrollRef = useRef(null);

  const getScrollableParent = (el) => {
    let node = el;
    while (node && node !== document.body) {
      const { overflowY } = window.getComputedStyle(node);
      if ((overflowY === 'auto' || overflowY === 'scroll') && node.scrollHeight > node.clientHeight) {
        return node; // เจอคอนเทนเนอร์ที่เลื่อนได้จริง
      }
      node = node.parentElement;
    }
    return window; // ถ้าไม่เจอ ให้เลื่อนทั้งหน้าต่าง
  };

  const scrollToTop = () => {
    const target = getScrollableParent(scrollRef.current);
    if (target === window) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      target.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const title = 'Doctor Calendar';
  const [previewImageUrl, setPreviewImageUrl] = useState(null);
  const [previewCaption, setPreviewCaption] = useState('');

  const images = assetData.doctor_calendar.map((item) => ({
    label: item.name,
    url: item.url,
    caption: item.textAfter || '',
  }));

  const doctorMembersImage = assetData.doctor_calendar.find(
    (item) => item.alias === 'doctor-members'
  );

  const sendToLine = async (text) => {
    const res = await fetch('https://therefineryclinic.herokuapp.com/sendToLine', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userId, message: text }),
    });

    const result = await res.json();
    if (!result.success) {
      throw new Error(result.error || 'ส่งข้อความไม่สำเร็จ');
    }
  };

  const sendAndLogText = async (text) => {
    await sendToLine(text);

    const latestCustomerInfo = selectedCustomer || {};

    await addDoc(collection(db, 'messages'), {
      from: 'me',
      role: 'me',
      userId,
      type: 'text',
      customer: text,
      createdAt: serverTimestamp(),
      displayName: latestCustomerInfo.displayName || 'Customer',
      pictureUrl: latestCustomerInfo.pictureUrl || '',
    });
  };

  const logImageToFirestore = async (imageUrl, label) => {
    const latestCustomerInfo = selectedCustomer || {};

    await addDoc(collection(db, 'messages'), {
      from: 'me',
      role: 'me',
      userId,
      type: 'image',
      imageUrl,
      createdAt: serverTimestamp(),
      displayName: latestCustomerInfo.displayName || 'Customer',
      pictureUrl: latestCustomerInfo.pictureUrl || '',
    });

    setSuccessMessage(`Image "${label}" from ${title} has been sent successfully.`);
    setTimeout(() => setSuccessMessage(''), 1800);
  };

  const sendImage = async (imageUrl) => {
    const res = await fetch('https://therefineryclinic.herokuapp.com/sendToLineImage', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userId, imageUrl }),
    });

    if (!res.ok) throw new Error('❌ ไม่สามารถส่งภาพได้');
  };

  const sendAndLogImage = async ({ imageUrl, label }) => {
    try {
      await sendImage(imageUrl);
      await logImageToFirestore(imageUrl, label);

      // ส่ง doctor-members ต่อ (ถ้าไม่ใช่ภาพเดียวกัน)
      if (doctorMembersImage && doctorMembersImage.url !== imageUrl) {
        await sendImage(doctorMembersImage.url);
        await logImageToFirestore(doctorMembersImage.url, doctorMembersImage.name);
      }
    } catch (err) {
      console.error(err);
      alert('❌ ไม่สามารถส่งภาพได้');
    }
  };

  const sendWithCaption = async ({ imageUrl, label, caption }) => {
    try {
      await sendImage(imageUrl);
      await logImageToFirestore(imageUrl, label);

      // ส่ง doctor-members ต่อ
      if (doctorMembersImage && doctorMembersImage.url !== imageUrl) {
        await sendImage(doctorMembersImage.url);
        await logImageToFirestore(doctorMembersImage.url, doctorMembersImage.name);
      }

      // ส่ง caption ต่อ
      if (caption) {
        await sendAndLogText(caption);
      }

      setSuccessMessage(`"${label}" with caption from ${title} has been sent successfully.`);
      setTimeout(() => setSuccessMessage(''), 1800);
    } catch (err) {
      console.error(err);
      alert('❌ ไม่สามารถส่งภาพ + ข้อความได้');
    }
  };

  const gridColsClass = {
    2: 'md:grid-cols-2',
    3: 'md:grid-cols-3',
    4: 'md:grid-cols-4',
  }[columns] || 'md:grid-cols-4';

  return (
    <div ref={scrollRef} className="relative max-w-screen-xl mx-auto px-4">
      <div className="flex justify-between items-center mt-[-2px] mb-4">
        <div className="flex items-center gap-3">
          <h3 className="text-lg font-semibold">{title}</h3>
          <button
            onClick={onBack}
            className="px-2 py-1 text-xs rounded-full border border-gray-300 text-gray-500 bg-gray-100 hover:bg-gray-200 transition"
          >
            ← Back
          </button>
        </div>

        <div className="flex items-center gap-2">
          {[4, 3, 2].map((col) => (
            <button
              key={col}
              onClick={() => setColumns(col)}
              className={`px-2 py-1 text-xs rounded-full border ${columns === col
                ? 'bg-black text-white border-black'
                : 'border-gray-300 text-gray-500 hover:bg-gray-100'
                }`}
            >
              {col} / row
            </button>
          ))}
        </div>
      </div>

      <div className={`grid grid-cols-2 gap-6 ${gridColsClass}`}>
        {images.map((img, index) => (
          <div
            key={index}
            className="flex flex-col items-stretch p-4 rounded-2xl border shadow-sm bg-white h-full"
          >
            {/* กล่องภาพสัดส่วนคงที่ → ปุ่มจะอยู่ระดับเดียวกันทุกใบ */}
            <div className="w-full aspect-[4/5] sm:aspect-[3/4] lg:aspect-[4/5] rounded-xl overflow-hidden bg-gray-50">
              <img
                src={img.url}
                alt={img.label}
                onClick={() => {
                  setPreviewImageUrl(img.url);
                  setPreviewCaption(img.caption || '');
                }}
                className="w-full h-full object-contain"
              />
            </div>

            <div className="mt-4 text-center text-sm text-gray-800">{img.label}</div>

            {/* ดันปุ่มไปก้นการ์ดเสมอ */}
            <div className="mt-auto w-full flex flex-col items-center">
              <button
                onClick={() => sendAndLogImage({ imageUrl: img.url, label: img.label })}
                className="mt-2 px-6 py-1.5 bg-gray-100 text-gray-800 rounded-full hover:bg-gray-200 transition text-sm"
              >
                Send
              </button>

              {(() => {
                const cap = typeof img.caption === 'string' ? img.caption.trim() : '';
                const hasCaption = cap !== '' && cap !== 'no-text';

                return hasCaption ? (
                  <button
                    onClick={() =>
                      sendWithCaption({ imageUrl: img.url, label: img.label, caption: img.caption })
                    }
                    className="mt-2 px-6 py-1.5 bg-black text-white rounded-full hover:opacity-80 transition text-sm"
                  >
                    + Caption
                  </button>
                ) : (
                  <button
                    disabled
                    className="mt-2 px-6 py-1.5 rounded-full border border-gray-300 text-gray-400 bg-gray-100 cursor-not-allowed opacity-60 text-sm"
                    title="This image has no caption"
                  >
                    No Caption
                  </button>
                );
              })()}
            </div>
          </div>
        ))}
      </div>

      {successMessage && (
        <div className="fixed inset-0 flex items-center justify-center z-50">
          <div className="bg-white shadow-xl rounded-xl px-6 py-4 border border-gray-200 text-center max-w-sm w-full">
            <div className="text-sm text-gray-800 mb-3 whitespace-pre-line">{successMessage}</div>
            <button
              onClick={() => setSuccessMessage('')}
              className="text-xs px-3 py-1 border border-gray-300 rounded-full hover:bg-gray-100 transition text-gray-500"
            >
              close
            </button>
          </div>
        </div>
      )}

      {/* Preview lightbox (click outside to close) */}
      {previewImageUrl && (
        <div
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
          onClick={() => {
            setPreviewImageUrl(null);
            setPreviewCaption('');
          }}
        >
          {/* ตัวครอบกลางไม่กินคลิก */}
          <div className="pointer-events-none max-w-5xl w-full flex justify-center">
            {/* กล่องรูป + แคปชัน: เป็น relative เพื่อให้ caption absolute อิงกล่องนี้ */}
            <div
              className="pointer-events-auto relative inline-block"
              onClick={(e) => e.stopPropagation()} // กันปิดเมื่อคลิกในกรอบ
            >
              <img
                src={previewImageUrl}
                alt="Preview"
                className="max-h-[70vh] w-auto rounded-xl shadow-2xl"
              />

              {/* Caption: ลอยติดรูปเสมอ และกว้าง 90% ของรูป */}
              {previewCaption && (
                <div
                  onClick={(e) => e.stopPropagation()}
                  className="
                    absolute bottom left-1/2 -translate-x-1/2
                    w-[100%] max-w-[22rem] max-h-28 overflow-y-auto
                    bg-white/90 backdrop-blur border border-gray-200
                    rounded-xl shadow-lg px-3 py-2
                    text-[13px] leading-snug text-gray-900
                    whitespace-pre-line
                  "
                >
                  {previewCaption}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="mt-6 flex justify-between items-center">
        {/* ปุ่ม Back ล่างซ้าย */}
        <button
          onClick={onBack}
          className="px-2 py-1 text-xs rounded-full border border-gray-300 
             text-gray-500 bg-gray-100 hover:bg-gray-200 transition"
        >
          ← Back
        </button>

        {/* ปุ่ม Top ล่างขวา */}
        <button
          onClick={scrollToTop}
          className="px-2 py-1 text-xs rounded-full border border-gray-300 
             text-gray-500 bg-gray-100 hover:bg-gray-200 transition"
        >
          ↑ Top
        </button>
      </div>

    </div>
  );
}
